import { useTranslation } from 'react-i18next';
import {
  alpha,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useBreakpoints,
  useTheme,
} from '@monorepo/nova-ui';
import { useUserCreditHistory } from '../../api/queries/user-credit-history-queries';
import { formatAmount, formatDate } from './balance-utils';

const BalanceCredits = ({ userId }: { userId?: string } = {}) => {
  const { palette, typography } = useTheme();
  const { data, isPending } = useUserCreditHistory(userId);
  const { isMobile } = useBreakpoints();
  const { t } = useTranslation('hubCommon');
  const credits = data?.credits;

  if (isPending)
    return <Skeleton variant="rectangular" width="100%" height={120} />;

  if (!credits) return null;

  return (
    <TableContainer
      sx={{
        paddingInline: typography.pxToRem(16),
        borderRadius: typography.pxToRem(16),
        border: `${typography.pxToRem(1)} solid ${palette.text.fifth}`,
        overflowX: 'auto',
      }}
    >
      <Table
        sx={{
          width: '100%',
          minWidth: isMobile ? 600 : '100%',
          tableLayout: isMobile ? 'auto' : 'fixed',
          '& .MuiTableCell-root': {
            whiteSpace: 'nowrap',
          },
          '& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root': {
            borderBottom: `${typography.pxToRem(1)} solid ${palette.divider}`,
          },
        }}
        aria-label="balance credits table"
      >
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                width: '20%',
                '&::first-letter': { textTransform: 'uppercase' },
              }}
            >
              {t('amount')}
            </TableCell>
            <TableCell
              sx={{
                width: '20%',
                '&::first-letter': { textTransform: 'uppercase' },
              }}
            >
              {t('used')}
            </TableCell>
            <TableCell
              sx={{
                width: '20%',
                '&::first-letter': { textTransform: 'uppercase' },
              }}
            >
              {t('remained')}
            </TableCell>
            <TableCell
              sx={{
                width: '20%',
                '&::first-letter': { textTransform: 'uppercase' },
              }}
            >
              {t('expiration')}
            </TableCell>
            <TableCell
              sx={{
                width: '20%',
                '&::first-letter': { textTransform: 'uppercase' },
              }}
            >
              {t('available')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {credits.map((transaction) => (
            <TableRow key={transaction._id}>
              <TableCell component="th" scope="row">
                {formatAmount(transaction.amount)}
              </TableCell>
              <TableCell>{formatAmount(transaction.used)}</TableCell>
              <TableCell>
                {formatAmount(transaction.amount - transaction.used)}
              </TableCell>
              <TableCell>
                {formatDate(new Date(transaction.expirationDate))}
              </TableCell>
              <TableCell>
                <Typography
                  sx={{
                    padding: typography.pxToRem(4),
                    paddingRight: typography.pxToRem(8),
                    borderRadius: 2,
                    fontSize: typography.pxToRem(14),
                    borderColor: transaction.expired
                      ? palette.error.main
                      : palette.success.main,
                    borderWidth: 2,
                    borderStyle: 'solid',
                    backgroundColor: transaction.expired
                      ? alpha(palette.error.main, 0.2)
                      : alpha(palette.success.main, 0.2),
                    color: palette.text.main,
                    textAlign: 'center',
                    fontWeight: 'medium',
                  }}
                >
                  {transaction.expired
                    ? t('balance_no', { defaultValue: 'No' })
                    : t('balance_yes', { defaultValue: 'Yes' })}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default BalanceCredits;
